import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useSecondaryNavActions } from "@client/components/secondary-nav";
import { DataTable } from "@tubo/ui/components/ui/data-table";
import type { DataTableColumn } from "@tubo/ui/components/ui/data-table";
import { ListPageHeaderActions, ListSearchInput } from "@client/components/common/list-page-controls";
import { formatDate } from "@client/components/doc-center/shared/format-utils";
import { downloadBlob } from "@client/lib/download";
import { Button } from "@tubo/ui/components/ui/button";
import { Input } from "@tubo/ui/components/ui/input";
import { Badge } from "@tubo/ui/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tubo/ui/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@tubo/ui/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@tubo/ui/components/ui/alert-dialog";
import { Plus, FileDown, Ban, Package, FolderArchive, Trash2 } from "lucide-react";
import {
  useExportTaskList,
  useExportTaskCreate,
  useExportTaskCancel,
  useExportTaskDelete,
  Download,
  getExportTaskListQueryKey,
} from "@tubo/api-models/hooks/export-task-api.hooks";
import { useExtractionTaskList } from "@tubo/api-models/hooks/extraction-task-api.hooks";
import { useListTables } from "@tubo/api-models/hooks/query-api.hooks";
import type { ExportTaskListItemVO } from "@tubo/api-models/hooks/export-task-api.schemas";
import {
  ExportTaskStatusEnum,
  ExportSourceTypeEnum,
  SinkTypeEnum,
  FileExportFormat,
} from "@tubo/api-models/business/data-center/data-center.enums";
import { BatchExportDialog, FORMAT_OPTIONS } from "@client/components/data-center/batch-export-dialog";

export const Route = createFileRoute("/_app/data-center/exports")({
  component: ExportsPage,
});

const PAGE_SIZE = 20;

const EXPORT_STATUS_CONFIG: Record<
  ExportTaskStatusEnum,
  { labelKey: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  [ExportTaskStatusEnum.Pending]: { labelKey: "pending", variant: "secondary" },
  [ExportTaskStatusEnum.Processing]: { labelKey: "processing", variant: "default" },
  [ExportTaskStatusEnum.Completed]: { labelKey: "completed", variant: "outline" },
  [ExportTaskStatusEnum.Failed]: { labelKey: "failed", variant: "destructive" },
  [ExportTaskStatusEnum.Cancelled]: { labelKey: "cancelled", variant: "secondary" },
};

function isExportStatus(v: string): v is ExportTaskStatusEnum {
  return Object.values(ExportTaskStatusEnum).some((val) => val === v);
}

const EXPORT_EXTENSION_BY_MEDIA_TYPE: Record<string, string> = {
  "application/zip": "zip",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "text/markdown": "md",
  "application/json": "json",
  "text/csv": "csv",
};

function getDownloadExtension(blob: Blob): string {
  const mediaType = blob.type.split(";", 1)[0].toLowerCase();
  return EXPORT_EXTENSION_BY_MEDIA_TYPE[mediaType] ?? "bin";
}

function ExportsPage() {
  const { t, i18n } = useTranslation("dataCenter");
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ExportTaskListItemVO | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useExportTaskList({ page, pageSize: pageSize });
  const items = useMemo(() => data?.items ?? [], [data]);
  // 后端 list 仅支持 status/page/pageSize，名称搜索做客户端过滤
  const filteredItems = useMemo(() => {
    if (!search) return items;
    const needle = search.toLowerCase();
    return items.filter((i) => i.name.toLowerCase().includes(needle));
  }, [items, search]);

  const invalidateList = () =>
    queryClient.invalidateQueries({ queryKey: getExportTaskListQueryKey() });

  const cancelMutation = useExportTaskCancel({
    mutation: {
      onSuccess: () => {
        toast.success(t("exports.cancelSuccess"));
        void invalidateList();
      },
      onError: () => toast.error(t("exports.cancelFailed")),
    },
  });

  const deleteMutation = useExportTaskDelete({
    mutation: {
      onSuccess: () => {
        toast.success(t("exports.deleteSuccess"));
        setDeleteTarget(null);
        void invalidateList();
      },
      onError: () => toast.error(t("exports.deleteFailed")),
    },
  });

  // 每行动态 id 的命令式下载，不适合 useQuery hook 形态，走生成的裸 fetcher
  const handleDownload = useCallback(async (id: string, name: string) => {
    try {
      const blob = await Download(id);
      downloadBlob(blob, `${name}.${getDownloadExtension(blob)}`);
    } catch {
      toast.error(t("exports.downloadFailed"));
    }
  }, [t]);

  const columns: DataTableColumn<ExportTaskListItemVO>[] = useMemo(
    () => [
      {
        id: "name",
        header: t("exports.name"),
        cell: (row) => (
          <button
            type="button"
            className="inline-flex min-w-0 items-center gap-1.5 rounded font-medium hover:underline"
            title={t("exports.viewDetails")}
            onClick={() =>
              navigate({ to: "/data-center/exports/$exportId", params: { exportId: row.id } })
            }
          >
            <span className="truncate">{row.name}</span>
            {(row.sourceCount ?? 1) > 1 && (
              <span title={t("exports.batchTasks", { count: row.sourceCount })} className="inline-flex">
                <Package className="h-3.5 w-3.5 shrink-0 text-primary" />
              </span>
            )}
          </button>
        ),
      },
      {
        id: "status",
        header: t("exports.status"),
        align: "center",
        className: "w-24",
        cell: (row) => {
          const config = isExportStatus(row.status)
            ? EXPORT_STATUS_CONFIG[row.status]
            : undefined;
          return config ? (
            <Badge variant={config.variant}>{t(`exports.${config.labelKey}`)}</Badge>
          ) : (
            row.status
          );
        },
      },
      {
        id: "rows",
        header: t("exports.exportedRows"),
        align: "center",
        className: "w-24",
        cell: (row) => (
          <span className="text-sm tabular-nums">
            {row.exportedRows ?? row.totalRows ?? 0}
          </span>
        ),
      },
      {
        id: "createdAt",
        header: t("exports.createdAt"),
        className: "w-40",
        cell: (row) => (
          <span className="text-xs text-muted-foreground">
            {formatDate(row.createdAt, locale)}
          </span>
        ),
      },
      {
        id: "actions",
        header: t("exports.actions"),
        align: "center",
        className: "w-24",
        cell: (row) => (
          <div className="flex h-8 items-center justify-center gap-1">
            {row.status === ExportTaskStatusEnum.Completed && row.sinkType === SinkTypeEnum.File && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                title={t("exports.download")}
                onClick={() => void handleDownload(row.id, row.name)}
              >
                <FileDown className="h-4 w-4" />
              </Button>
            )}
            {(row.status === ExportTaskStatusEnum.Pending ||
              row.status === ExportTaskStatusEnum.Processing) && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                title={t("exports.cancel")}
                disabled={cancelMutation.isPending}
                onClick={() => cancelMutation.mutate({ id: row.id })}
              >
                <Ban className="h-4 w-4" />
              </Button>
            )}
            {row.isBatch &&
              (row.status === ExportTaskStatusEnum.Completed ||
                row.status === ExportTaskStatusEnum.Failed ||
                row.status === ExportTaskStatusEnum.Cancelled) && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  title={t("exports.delete")}
                  onClick={() => setDeleteTarget(row)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
          </div>
        ),
      },
    ],
    [cancelMutation, locale, navigate, t, handleDownload],
  );

  useSecondaryNavActions(
    <ListPageHeaderActions>
      <ListSearchInput
        placeholder={t("exports.search")}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
        }}
      />
      <Button size="sm" onClick={() => setShowCreate(true)}>
        <Plus className="mr-1.5 h-4 w-4" />
        {t("exports.create")}
      </Button>
      <Button size="sm" variant="outline" onClick={() => setShowBatch(true)}>
        <FolderArchive className="mr-1.5 h-4 w-4" />
        {t("exports.batchExport")}
      </Button>
    </ListPageHeaderActions>,
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <DataTable
        columns={columns}
        data={filteredItems}
        rowKey={(row) => row.id}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        total={data?.total ?? 0}
        onPageChange={setPage}
        isLoading={isLoading}
        emptyState={
          <div className="flex h-64 flex-col items-center justify-center text-muted-foreground">
            <FileDown className="mb-3 h-12 w-12 text-icon-secondary-muted" />
            <p className="text-sm font-medium">{t("exports.empty")}</p>
            <p className="text-xs text-muted-foreground/70">
              {t("exports.emptyDescription")}
            </p>
          </div>
        }
      />

      <CreateExportDialog open={showCreate} onOpenChange={setShowCreate} />
      <BatchExportDialog open={showBatch} onOpenChange={setShowBatch} />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("exports.deleteDialogTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("exports.deleteDialogMessage", { name: deleteTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("exports.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              onClick={() =>
                deleteTarget && deleteMutation.mutate({ id: deleteTarget.id })
              }
            >
              {t("exports.confirmDelete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateExportDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation("dataCenter");
  const queryClient = useQueryClient();
  const [sourceType, setSourceType] = useState<ExportSourceTypeEnum>(
    ExportSourceTypeEnum.ExtractionTask,
  );
  const [name, setName] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [format, setFormat] = useState<FileExportFormat>(FileExportFormat.CSV);
  const [taskSearch, setTaskSearch] = useState("");

  // 后端搜索（D31⑥）：老任务可搜到；全量渲染，不可导出任务仅 disabled 不隐藏
  const { data: taskData } = useExtractionTaskList(
    { page: 1, pageSize: 100, ...(taskSearch ? { search: taskSearch } : {}) },
    { query: { enabled: open && sourceType === ExportSourceTypeEnum.ExtractionTask } },
  );
  const tasks = useMemo(() => taskData?.items ?? [], [taskData]);

  // 结构化表来源：已注册的数据表（含表格抽取任务物化的数据集）
  const { data: tableData } = useListTables(
    { page: 1, pageSize: 100 },
    { query: { enabled: open && sourceType === ExportSourceTypeEnum.DataTable } },
  );
  const tables = useMemo(
    () => (tableData?.items ?? []).filter((t) => !!t.sourceId),
    [tableData],
  );

  const createMutation = useExportTaskCreate({
    mutation: {
      onSuccess: () => {
        toast.success(t("exports.createSuccess"));
        onOpenChange(false);
        setName("");
        setSourceId("");
        setFormat(FileExportFormat.CSV);
        setTaskSearch("");
        void queryClient.invalidateQueries({
          queryKey: getExportTaskListQueryKey(),
        });
      },
      onError: () => toast.error(t("exports.createFailed")),
    },
  });

  const handleSubmit = () => {
    createMutation.mutate({
      data: {
        name: name.trim(),
        sourceType,
        sourceId,
        sinkType: SinkTypeEnum.File,
        sinkConfig: { format },
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("exports.newTaskTitle")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("exports.taskName")}</label>
            <Input
              placeholder={t("exports.taskNamePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("exports.dataSource")}</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setSourceType(ExportSourceTypeEnum.ExtractionTask);
                  setSourceId("");
                }}
                className={`flex-1 rounded-lg border p-2.5 text-left text-sm transition-colors ${
                  sourceType === ExportSourceTypeEnum.ExtractionTask
                    ? "border-primary bg-primary/5 font-medium"
                    : "border-border-muted hover:bg-surface"
                }`}
              >
                {t("exports.extractionTask")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSourceType(ExportSourceTypeEnum.DataTable);
                  setSourceId("");
                }}
                className={`flex-1 rounded-lg border p-2.5 text-left text-sm transition-colors ${
                  sourceType === ExportSourceTypeEnum.DataTable
                    ? "border-primary bg-primary/5 font-medium"
                    : "border-border-muted hover:bg-surface"
                }`}
              >
                {t("exports.structuredTable")}
              </button>
            </div>
          </div>
          {sourceType === ExportSourceTypeEnum.ExtractionTask ? (
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("exports.extractionTask")}</label>
              <ListSearchInput
                className="w-full"
                placeholder={t("exports.searchTasks")}
                value={taskSearch}
                onChange={(e) => setTaskSearch(e.target.value)}
              />
              <Select value={sourceId} onValueChange={(v) => setSourceId(v ?? "")}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={tasks.length === 0 ? t("exports.noTasks") : t("exports.chooseExtractionTask")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {tasks.map((task) => (
                    <SelectItem
                      key={task.id}
                      value={task.id}
                      disabled={(task.reviewTotalCount ?? 0) === 0}
                    >
                      {task.name}（{t("exports.taskSuccessCount", { completed: task.completedCount, total: task.totalFiles })}）
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("exports.structuredTable")}</label>
              <Select value={sourceId} onValueChange={(v) => setSourceId(v ?? "")}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={tables.length === 0 ? t("exports.noStructuredTables") : t("exports.chooseStructuredTable")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {tables.map((table) => (
                    <SelectItem key={table.id} value={table.id}>
                      {table.displayName}
                      {table.sourceType === "assetView" ? t("exports.assetView") : ""}（{t("exports.rowCount", { count: table.rowCount ?? 0 })}）
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t("exports.assetViewDescription")}
              </p>
            </div>
          )}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("exports.fileFormat")}</label>
            <Select
              items={FORMAT_OPTIONS}
              value={format}
              onValueChange={(v) => {
                const hit = FORMAT_OPTIONS.find((o) => o.value === v);
                if (hit) setFormat(hit.value);
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FORMAT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("exports.cancelCreate")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || !sourceId || createMutation.isPending}
          >
            {t("exports.createButton")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
